// Static content for the emotion wheel.
//
// Source of truth: ai-promo/emotion-wheel-data.json (kept in the handoff
// folder as the reusable, non-React copy). This file is a direct port of
// that JSON into a JS module so it can be imported without a fetch/build
// step - matching how other static, non-Contentful content lives directly
// in website/src (e.g. plain JS/JSON files imported by components) rather
// than coming from GraphQL.
//
// Cores are listed CLOCKWISE starting at the top (12 o'clock). Within each
// core, branches are in clockwise order, and each branch's two outer
// feelings are also in clockwise order. This matches the wheel's render
// order exactly.

export const wheelGeometry = {
  viewBox: [0, 0, 800, 800],
  center: [400, 400],
  radii: { core: 125, branchOuter: 250, outerOuter: 372 },
}

export const wheelCores = [
  {
    name: 'Sad',
    colors: { core: '#4FA3E3', branch: '#9BC9EF', outer: '#CFE4F7' },
    branches: [
      { name: 'Lonely', outer: ['Isolated', 'Abandoned'] },
      { name: 'Vulnerable', outer: ['Fragile', 'Defenseless'] },
      { name: 'Despair', outer: ['Powerless', 'Grief-stricken'] },
      { name: 'Guilty', outer: ['Remorseful', 'Ashamed'] },
      { name: 'Depressed', outer: ['Empty', 'Melancholy'] },
    ],
  },
  {
    name: 'Fear',
    colors: { core: '#4CAF6E', branch: '#93D3A6', outer: '#CDEBD5' },
    branches: [
      { name: 'Scared', outer: ['Terrified', 'Panicked'] },
      { name: 'Anxious', outer: ['Overwhelmed', 'Worried'] },
      { name: 'Insecure', outer: ['Inadequate', 'Inferior'] },
      { name: 'Weak', outer: ['Worthless', 'Helpless'] },
      { name: 'Rejected', outer: ['Excluded', 'Persecuted'] },
    ],
  },
  {
    name: 'Disgust',
    colors: { core: '#F5A623', branch: '#F7C577', outer: '#FBE3B8' },
    branches: [
      { name: 'Repelled', outer: ['Revolted', 'Nauseated'] },
      { name: 'Disapproving', outer: ['Judgmental', 'Embarrassed'] },
      { name: 'Awful', outer: ['Horrified', 'Abominable'] },
      { name: 'Avoidant', outer: ['Hesitant', 'Distant'] },
      { name: 'Disdainful', outer: ['Contemptuous', 'Dismissive'] },
    ],
  },
  {
    name: 'Surprise',
    colors: { core: '#9B6FC4', branch: '#C3A5DE', outer: '#E1D2EF' },
    branches: [
      { name: 'Moved', outer: ['Stimulated', 'Touched'] },
      { name: 'Excited', outer: ['Energetic', 'Eager'] },
      { name: 'Amazed', outer: ['Awe-struck', 'Astonished'] },
      { name: 'Confused', outer: ['Perplexed', 'Disoriented'] },
      { name: 'Startled', outer: ['Dismayed', 'Shocked'] },
    ],
  },
  {
    name: 'Anger',
    colors: { core: '#EF5350', branch: '#F49B99', outer: '#F8CFCE' },
    branches: [
      { name: 'Critical', outer: ['Skeptical', 'Sarcastic'] },
      { name: 'Hateful', outer: ['Scornful', 'Loathing'] },
      { name: 'Frustrated', outer: ['Infuriated', 'Annoyed'] },
      { name: 'Hostile', outer: ['Bitter', 'Aggressive'] },
      { name: 'Provoked', outer: ['Resentful', 'Aggravated'] },
    ],
  },
  {
    name: 'Happy',
    colors: { core: '#FFD93B', branch: '#FCE07A', outer: '#FBF0BE' },
    branches: [
      { name: 'Joyful', outer: ['Elated', 'Radiant'] },
      { name: 'Interested', outer: ['Inquisitive', 'Curious'] },
      { name: 'Proud', outer: ['Illustrious', 'Triumphant'] },
      { name: 'Content', outer: ['Satisfied', 'Peaceful'] },
      { name: 'Optimistic', outer: ['Inspired', 'Hopeful'] },
    ],
  },
]

// Real team responses (replaces the earlier invented placeholder
// distribution). Each `emotion` must match a label in `wheelCores` exactly
// (it's used as the key into the wheel's label centroids to place the dot).
// `color` is per-person so dots can be told apart on the board; `note` is
// the person's own words, lightly cleaned up for display (typos fixed,
// sentence-cased) but not otherwise rewritten.
//
// Three of these (see NARRATIVE_PICKS below) are earmarked as the ones to
// carry into the scrolling narrative later - the rest still get a dot on the
// wheel, just no featured quote.
export const teamResponses = [
  {
    person: 'Lucy',
    color: '#D6336C',
    // Placeholder headshot - swap for the real photo. Generated from the
    // person's own color so it doesn't look out of place in the meantime.
    image:
      'https://images.ctfassets.net/btqy35w0l487/6QAxZmvMcBhQv2dmvoWIR5/f4e4b554107c4a90bfc5565960d433d2/Rachel_Loughrey.jpg?w=1200&h=1200&q=50&fm=webp',
    responses: [
      { emotion: 'Disoriented', note: '' },
      { emotion: 'Confused', note: '' },
      {
        emotion: 'Bitter',
        note: 'I studied art and have always loved creativity, so watching AI take over creative work stings. I am scared it will come for our creativity and take away something that makes us human.',
      },
      {
        emotion: 'Interested',
        note: 'I am using it a lot now and it does really help me think stuff through.',
      },
    ],
  },
  {
    person: 'Piotr',
    color: '#1098AD',
    // Placeholder headshot - swap for the real photo.
    image:
      'https://images.ctfassets.net/btqy35w0l487/227ib6K0ks4xrQV13I0xhl/97d67f1d72050165e5548758a74f4df8/Piotr.jpeg?w=500&h=500&q=50&fm=webp',
    responses: [
      { emotion: 'Worried', note: 'What this will do to our society.' },
      {
        emotion: 'Curious',
        note: "I recently started a course on AI and sustainability, and I'm really enjoying it. I love learning new things, and getting past my initial hesitation about AI has left me feeling more hopeful.",
      },
      {
        emotion: 'Provoked',
        note: 'I have a daughter and am really unsure what this will do to her future.',
      },
    ],
  },
  {
    person: 'Timo',
    color: '#5F3DC4',
    // Placeholder headshot - swap for the real photo.
    image:
      'https://images.ctfassets.net/btqy35w0l487/dzoK50j2RhJxqkz7KqJJA/f0797ed867585ff4b63fd16afb0fc042/IMG_8514.JPG?w=1200&h=1200&q=50&fm=webp',
    responses: [
      {
        emotion: 'Bitter',
        note: "AI can already write better code, faster, than the 15 years it took me to learn how. I'm grateful for what it can do - but I'd be lying if I said that didn't sting a little.",
      },
      {
        emotion: 'Hopeful',
        note: "Honestly, the way AI is reshaping our society terrifies me - but I'm an optimist, and I'm not ready to give up hope.",
      },
      {
        emotion: 'Proud',
        note: 'I feel weirdly proud when I create something with AI - even if a lot of the work is done by AI, I kind of feel ownership for it?',
      },
    ],
  },
  {
    person: 'Luce',
    color: '#087F5B',
    // Placeholder headshot - swap for the real photo.
    image:
      'https://images.ctfassets.net/btqy35w0l487/qXijUs4xvXuMvtnXfd0Ty/8d4b29a69febdafbfca52135276e0293/Lucie_Pourquier_LinkedIn.jpeg?w=600&h=600&q=50&fm=webp',
    responses: [
      {
        emotion: 'Nauseated',
        note: 'A weird mix of disgust - something that pushes me off it.',
      },
      {
        emotion: 'Satisfied',
        note: "Incredibly useful - I use it so much, especially since I'm not a native English speaker.",
      },
      {
        emotion: 'Vulnerable',
        note: "I use AI because I feel like I have to - it's just too good to ignore. But it makes me feel vulnerable, and I fear for my job and my future.",
      },
    ],
  },
]

// The three stops in the scroll story, in the order the wheel visits them.
// A recommendation, not a final call - see chat for reasoning on why these
// three. Swap freely; { person, emotion } must match an entry above.
export const NARRATIVE_PICKS = [
  { person: 'Luce', emotion: 'Vulnerable' },
  { person: 'Timo', emotion: 'Proud' },
  { person: 'Lucy', emotion: 'Bitter' },
]
